import type {
  BulkUploadGlobalError,
  BulkUploadMode,
  BulkUploadPreviewContextWsDTO,
  BulkUploadRowErrorWsDTO,
  BulkUploadSuccessWsDTO,
  OrdinaryBulkUploadSource,
} from "../types/contracts";

type BulkUploadWorkflowIdentity =
  | {
      mode: "ordinary";
      source: OrdinaryBulkUploadSource;
    }
  | {
      mode: "merge";
      source: "merge";
    };

export interface BulkUploadWorkflowFailure {
  kind: "upload" | "validation" | "business" | "transport";
  error?: BulkUploadGlobalError;
  rowErrors?: BulkUploadRowErrorWsDTO[];
}

export type BulkUploadBeforePreviewState = BulkUploadWorkflowIdentity & {
  phase: "beforePreview";
  notice?: "contextChanged";
  failure?: BulkUploadWorkflowFailure;
};

export type BulkUploadPreviewLoadingState = BulkUploadWorkflowIdentity & {
  phase: "loading";
  operation: "preview";
};

export type BulkUploadPreviewState = BulkUploadWorkflowIdentity & {
  phase: "preview";
  previewContext: BulkUploadPreviewContextWsDTO;
  trackingUid?: string;
  saleOnly: boolean;
  overrideConfirmationOpen: boolean;
  failure?: BulkUploadWorkflowFailure;
};

export type BulkUploadAddLoadingState = BulkUploadWorkflowIdentity & {
  phase: "loading";
  operation: "add";
  previewContext: BulkUploadPreviewContextWsDTO;
  trackingUid?: string;
  saleOnly: boolean;
};

export type BulkUploadCompleteState = BulkUploadWorkflowIdentity & {
  phase: "complete";
  success: BulkUploadSuccessWsDTO;
};

export type BulkUploadWorkflowState =
  | BulkUploadBeforePreviewState
  | BulkUploadPreviewLoadingState
  | BulkUploadPreviewState
  | BulkUploadAddLoadingState
  | BulkUploadCompleteState;

export type BulkUploadWorkflowAction =
  | {
      type: "sourceSelected";
      source: OrdinaryBulkUploadSource;
    }
  | {
      type: "previewRequested";
    }
  | {
      type: "previewReceived";
      previewContext: BulkUploadPreviewContextWsDTO;
      trackingUid?: string;
    }
  | {
      type: "saleOnlyChanged";
      value: boolean;
    }
  | {
      type: "overrideConfirmationOpened";
    }
  | {
      type: "overrideConfirmationClosed";
    }
  | {
      type: "addRequested";
    }
  | {
      type: "requestFailed";
      failure: BulkUploadWorkflowFailure;
    }
  | {
      type: "contextChanged";
    }
  | {
      type: "addSucceeded";
      success: BulkUploadSuccessWsDTO;
    }
  | {
      type: "lastRowRemoved" | "discarded" | "closed";
    };

export function createInitialBulkUploadState(
  mode: "ordinary",
  source?: OrdinaryBulkUploadSource,
): BulkUploadBeforePreviewState;
export function createInitialBulkUploadState(mode: "merge"): BulkUploadBeforePreviewState;
export function createInitialBulkUploadState(
  mode: BulkUploadMode,
  source: OrdinaryBulkUploadSource = "file",
): BulkUploadBeforePreviewState {
  return mode === "merge"
    ? {
        mode,
        phase: "beforePreview",
        source: "merge",
      }
    : {
        mode,
        phase: "beforePreview",
        source,
      };
}

export function bulkUploadReducer(
  state: BulkUploadWorkflowState,
  action: BulkUploadWorkflowAction,
): BulkUploadWorkflowState {
  switch (action.type) {
    case "sourceSelected":
      return selectSource(state, action.source);

    case "previewRequested":
      return requestPreview(state);

    case "previewReceived":
      return receivePreview(state, action.previewContext, action.trackingUid);

    case "saleOnlyChanged":
      return changeSaleOnly(state, action.value);

    case "overrideConfirmationOpened":
      return changeOverrideConfirmation(state, true);

    case "overrideConfirmationClosed":
      return changeOverrideConfirmation(state, false);

    case "addRequested":
      return requestAdd(state);

    case "requestFailed":
      return failRequest(state, action.failure);

    case "contextChanged":
      return {
        ...resetWorkflow(state),
        notice: "contextChanged",
      };

    case "addSucceeded":
      return completeAdd(state, action.success);

    case "lastRowRemoved":
    case "discarded":
    case "closed":
      return resetWorkflow(state);
  }
}

function selectSource(state: BulkUploadWorkflowState, source: OrdinaryBulkUploadSource): BulkUploadWorkflowState {
  return state.mode === "ordinary" && state.phase === "beforePreview"
    ? createInitialBulkUploadState("ordinary", source)
    : state;
}

function requestPreview(state: BulkUploadWorkflowState): BulkUploadWorkflowState {
  if (state.phase !== "beforePreview") {
    return state;
  }
  return {
    mode: state.mode,
    phase: "loading",
    operation: "preview",
    source: state.source,
  } as BulkUploadPreviewLoadingState;
}

function receivePreview(
  state: BulkUploadWorkflowState,
  previewContext: BulkUploadPreviewContextWsDTO,
  trackingUid?: string,
): BulkUploadWorkflowState {
  if (state.phase !== "loading" || state.operation !== "preview") {
    return state;
  }
  return {
    mode: state.mode,
    phase: "preview",
    source: state.source,
    previewContext,
    ...(state.mode === "ordinary" && trackingUid ? { trackingUid } : {}),
    saleOnly: false,
    overrideConfirmationOpen: false,
  } as BulkUploadPreviewState;
}

function changeSaleOnly(state: BulkUploadWorkflowState, saleOnly: boolean): BulkUploadWorkflowState {
  return state.phase === "preview"
    ? {
        ...state,
        saleOnly,
      }
    : state;
}

function changeOverrideConfirmation(
  state: BulkUploadWorkflowState,
  overrideConfirmationOpen: boolean,
): BulkUploadWorkflowState {
  if (state.phase !== "preview" || (overrideConfirmationOpen && state.mode !== "ordinary")) {
    return state;
  }
  return {
    ...state,
    overrideConfirmationOpen,
  };
}

function requestAdd(state: BulkUploadWorkflowState): BulkUploadWorkflowState {
  if (state.phase !== "preview") {
    return state;
  }
  return {
    mode: state.mode,
    phase: "loading",
    operation: "add",
    source: state.source,
    previewContext: state.previewContext,
    ...(state.trackingUid ? { trackingUid: state.trackingUid } : {}),
    saleOnly: state.saleOnly,
  } as BulkUploadAddLoadingState;
}

function failRequest(state: BulkUploadWorkflowState, failure: BulkUploadWorkflowFailure): BulkUploadWorkflowState {
  if (state.phase !== "loading") {
    return state;
  }
  if (state.operation === "preview") {
    return {
      ...resetWorkflow(state),
      failure,
    };
  }
  return {
    mode: state.mode,
    phase: "preview",
    source: state.source,
    previewContext: state.previewContext,
    ...(state.trackingUid ? { trackingUid: state.trackingUid } : {}),
    saleOnly: state.saleOnly,
    overrideConfirmationOpen: false,
    failure,
  } as BulkUploadPreviewState;
}

function completeAdd(state: BulkUploadWorkflowState, success: BulkUploadSuccessWsDTO): BulkUploadWorkflowState {
  if (state.phase !== "loading" || state.operation !== "add") {
    return state;
  }
  return {
    mode: state.mode,
    phase: "complete",
    source: state.source,
    success,
  } as BulkUploadCompleteState;
}

function resetWorkflow(state: BulkUploadWorkflowState): BulkUploadBeforePreviewState {
  return state.mode === "merge"
    ? createInitialBulkUploadState("merge")
    : createInitialBulkUploadState("ordinary", state.source);
}
